import { Box, Divider, Stack, styled, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { allIcons, allImages } from "../../../assets";
import RenderReviewCard from "../../../components/ReviewCard";
import TeamCard from "../../../components/TeamCard";
import appcolors from "../../../constants/Colors";

const MobileContainer = styled(Stack)(({ theme }) => ({
  border: "1px solid #A6A9AC",
  borderRadius: "40px",
  padding: "10px 40px",
  [theme.breakpoints.down("md")]: {
    padding: "10px 20px",
  },
}));

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;

  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

const MobileScreen = ({ data, services }) => {
  const [serviceCategory, setServiceCategory] = useState([]);
  const mapRef = useRef(null);

  useEffect(() => {
    setServiceCategory(services[0]?.services);
  }, [services, data]);

  const checkStatus = (schedule) => {
    const currentTime = moment();

    const currentDay = currentTime?.format("dddd")?.toLowerCase();

    const todaySchedule = schedule?.find(
      (day) => day?.day?.toLowerCase() === currentDay
    );

    const getNextOpeningDay = (schedule, currentDay) => {
      const daysOfWeek = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const currentDayIndex = daysOfWeek?.indexOf(currentDay);

      let nextOpeningDay = null;
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (currentDayIndex + i) % 7;
        const nextDay = daysOfWeek[nextDayIndex];
        if (schedule?.some((day) => day?.day?.toLowerCase() === nextDay)) {
          nextOpeningDay = nextDay;
          break;
        }
      }

      return nextOpeningDay;
    };

    if (!todaySchedule) {
      const nextOpeningDay = getNextOpeningDay(schedule, currentDay);
      const nextOpeningTime = schedule?.find(
        (day) => day?.day?.toLowerCase() === nextOpeningDay
      )?.startTime;
      return `Close,Open ${moment(nextOpeningTime)?.format("hh:mm A")}`;
    }

    const { startTime, endTime } = todaySchedule;

    const openingMoment = moment(startTime);
    const closingMoment = moment(endTime);

    if (closingMoment?.isBefore(openingMoment)) {
      closingMoment?.add(1, "day");
    }

    if (currentTime?.isBetween(openingMoment, closingMoment)) {
      const formattedClosingTime = closingMoment?.format("hh:mm A");
      return `Open,Close ${formattedClosingTime}`;
    } else {
      const nextOpeningTime = moment(startTime)
        ?.add(1, "day")
        ?.format("hh:mm A");
      return `Closed, opens at ${nextOpeningTime}`;
    }
  };

  useEffect(() => {
    if (data?.businessDetails) {
      const newRegion = {
        latitude:
          (parseFloat(data?.businessDetails?.lat || 0) +
            parseFloat(data?.businessDetails?.lat || 0)) /
          2,
        longitude:
          (parseFloat(data?.businessDetails?.long || 0) +
            parseFloat(data?.businessDetails?.long || 0)) /
          2,
        latitudeDelta:
          Math.abs(
            parseFloat(data?.businessDetails?.lat || 0) -
              parseFloat(data?.businessDetails?.lat || 0)
          ) * 1.2,
        longitudeDelta:
          Math.abs(
            parseFloat(data?.businessDetails?.long || 0) -
              parseFloat(data?.businessDetails?.long || 0)
          ) * 1.2,
      };
      mapRef.current?.animateToRegion(newRegion, 1000);
    }
  }, [data?.businessDetails]);

  const calculateAverageRating = (reviews) => {
    const totalReviews = reviews?.reduce((sum, review) => {
      return sum + (review?.rating || 0);
    }, 0);
    const averageRating = reviews?.length
      ? (totalReviews / reviews?.length).toFixed(2)
      : "0.00";

    return averageRating;
  };

  return (
    <MobileContainer>
      <Stack
        mb={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant={"body2"} color={"#2D2E2E"} fontWeight={500}>
          {moment(new Date()).format("h:mm")}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          <img
            src={allIcons.networkSignalIcon}
            alt={"network-signal-icon"}
            width={15}
          />
          <img
            src={allIcons.wifiSignalIcon}
            alt={"network-signal-icon"}
            width={15}
          />
          <img
            src={allIcons.fullBatteryIcon}
            alt={"network-signal-icon"}
            width={22}
          />
        </Stack>
      </Stack>
      <img src={data?.images[0]} alt={"banner-slider"} />
      <Stack spacing={0.5} my={2}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            fontSize={"1.125rem"}
            fontWeight={600}
            direction={"row"}
            alignItems={"center"}
          >
            {data?.displayName}
          </Typography>
          <Stack direction={"row"} alignItems={"center"}>
            <Stack
              borderRadius={100}
              height={30}
              alignItems={"center"}
              justifyContent={"center"}
              width={30}
              border={"1px solid #999999"}
            >
              <img src={allIcons.orangeChat} height={20} width={20} />
            </Stack>
            <Stack
              borderRadius={100}
              height={30}
              alignItems={"center"}
              justifyContent={"center"}
              width={30}
              border={"1px solid #999999"}
              ml={1}
            >
              <img src={allIcons.iosShare} height={15} width={15} />
            </Stack>
          </Stack>
        </Stack>
        <Typography variant="body2" color={"#747677"}>
          {data?.location?.address}
        </Typography>
        <Typography variant="body2" color={"#747677"}>
          {checkStatus(data?.timmings)}
        </Typography>
      </Stack>

      <Divider />
      <Stack
        direction={"row"}
        alignItems={"center"}
        mt={2}
        justifyContent={"space-between"}
      >
        <Typography variant="body2" color={"#000"} fontWeight={600}>
          Service
        </Typography>
        <Typography
          variant="body2"
          color={"#000"}
          sx={{ textDecoration: "underline" }}
        >
          Show All
        </Typography>
      </Stack>
      <Stack>
        {services?.length > 0 ? (
          <>
            <Stack direction={"row"} alignItems={"center"} mt={2}>
              <Stack
                width={500}
                direction={"row"}
                alignItems={"center"}
                overflow={"auto"}
                sx={{
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                {services?.map((item, index) => {
                  return (
                    <Stack
                      key={index}
                      padding={1}
                      borderRadius={100}
                      bgcolor={appcolors.primary}
                      flexShrink={0}
                      marginRight={1}
                    >
                      <Typography
                        variant="body1"
                        textAlign={"center"}
                        color={"#FFF"}
                        sx={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item?.name}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
            <Stack mt={2}>
              {serviceCategory?.map((item, index) => {
                return (
                  <Stack
                    key={index}
                    width={"100%"}
                    height={80}
                    direction={"row"}
                  >
                    <Stack width={220}>
                      <Typography
                        variant="body1"
                        color={"#000"}
                        sx={{
                          textDecoration: "underline",
                        }}
                      >
                        {item?.serviceName}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={"#868E96"}
                        fontSize={12}
                        sx={{
                          textDecoration: "underline",
                        }}
                      >
                        {/* {item?.serviceDescription?.length > 60
                          ? `${item?.serviceDescription?.slice(0, 60)}...`
                          : item?.serviceDescription} */}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontSize={12}
                        color={"#868E96"}
                        sx={{
                          textDecoration: "underline",
                        }}
                      >
                        {item?.price} SAR
                      </Typography>
                      <Typography
                        variant="body1"
                        fontSize={12}
                        color={"#868E96"}
                        sx={{
                          textDecoration: "underline",
                        }}
                      >
                        {item?.duration} min
                      </Typography>
                    </Stack>
                    <Stack width={90}>
                      <Stack
                        key={index}
                        borderRadius={100}
                        bgcolor={appcolors.primary}
                        marginTop={3}
                      >
                        <Typography
                          variant="body1"
                          textAlign={"center"}
                          color={"#FFF"}
                          sx={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          Book
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </>
        ) : (
          <Typography
            variant="body1"
            fontSize={16}
            textAlign={"center"}
            color={appcolors.black}
            mt={5}
            mb={5}
          >
            No Service Found!
          </Typography>
        )}
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        mt={2}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} alignItems={"center"} mt={1}>
          <img src={allIcons.halfStar} height={25} width={25} />
          <Typography
            variant="body1"
            fontWeight={600}
            color={"#000"}
            sx={{
              textDecoration: "underline",
            }}
          >
            {`${calculateAverageRating(data?.branchReviews)} (${
              data?.branchReviews?.length || 0
            } Reviews)`}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color={"#000"}
          sx={{ textDecoration: "underline" }}
        >
          Show All
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "flex",
          overflowX: "scroll",
          gap: 2,
          padding: 2,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {data?.branchReviews?.length > 0 ? (
          <>
            {data?.branchReviews?.map((item, index) => (
              <RenderReviewCard key={index} item={item} />
            ))}
          </>
        ) : (
          <Typography
            variant="body1"
            fontSize={12}
            width={"100%"}
            textAlign={"center"}
            color={appcolors.black}
          >
            No Reviews yet!
          </Typography>
        )}
      </Box>
      <Stack
        direction={"row"}
        alignItems={"center"}
        mt={2}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} alignItems={"center"} mt={1}>
          <img src={allIcons.teamInactiveIcon} height={25} width={25} />
          <Typography
            variant="body1"
            fontWeight={600}
            color={"#000"}
            ml={1}
            sx={{
              textDecoration: "underline",
            }}
          >
            Our Team
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color={"#000"}
          sx={{ textDecoration: "underline" }}
        >
          Show All
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "flex",
          overflowX: "scroll",
          gap: 2,
          padding: 2,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
        }}
      >
        {data?.branchTeam?.length > 0 ? (
          <>
            {data?.branchTeam?.map((item, index) => (
              <TeamCard key={index} item={item} />
            ))}
          </>
        ) : (
          <Typography
            variant="body1"
            fontSize={12}
            width={"100%"}
            textAlign={"center"}
            color={appcolors.black}
          >
            No team member found!
          </Typography>
        )}
      </Box>
      <Stack my={2}>
        <Typography fontSize={"1.1rem"} fontWeight={600}>
          Location
        </Typography>
        <Box
          mt={0.5}
          height={200}
          border={"1px solid #EDF1F5"}
          borderRadius={"6px"}
          overflow={"hidden"}
        >
          <img
            src={allImages.mapImage}
            alt={"All-images"}
            width={"100%"}
            height={"100%"}
            style={{
              objectFit: "cover",
            }}
          />
        </Box>
        <Typography mt={1} fontSize={"0.75rem"} color={"#747677"}>
          {data?.location?.address}
        </Typography>
        <Typography
          fontSize={"0.8rem"}
          fontWeight={500}
          sx={{ textDecoration: "underline" }}
          marginLeft={"auto"}
        >
          Go to Maps
        </Typography>
      </Stack>
      <Typography fontSize={"1.1rem"} fontWeight={600}>
        Description
      </Typography>
      <Typography fontSize={"0.91rem"} fontWeight={400}>
        {data?.about}
      </Typography>
      <Stack mt={2}>
        <Typography fontSize={"1.1rem"} fontWeight={600}>
          Opening and closing time
        </Typography>
        {data?.timmings?.map((item) => {
          return (
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              mt={1}
              key={item?.name}
            >
              <Typography
                fontSize={"0.91rem"}
                fontWeight={400}
                sx={{
                  textDecoration: "underline",
                  color: appcolors.black,
                }}
              >
                {item?.name}
              </Typography>
              <Typography
                fontSize={"0.91rem"}
                fontWeight={400}
                sx={{
                  textDecoration: "underline",
                }}
                color={"#747677"}
              >
                {item?.startTime && item?.endTime
                  ? `${item?.startTime} To ${item?.endTime}`
                  : "Holiday"}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <Stack
        height={40}
        mt={2}
        mb={2}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box
          border={`1px solid #747677`}
          borderRadius={2}
          display={"flex"}
          alignContent={"center"}
          padding={1}
          width={135}
          justifyContent={"center"}
        >
          <Typography fontSize={"0.91rem"} fontWeight={400}>
            Back
          </Typography>
        </Box>
        <Box
          borderRadius={2}
          display={"flex"}
          border={`1px solid ${appcolors.primary}`}
          alignContent={"center"}
          padding={1}
          width={135}
          bgcolor={appcolors.primary}
          justifyContent={"center"}
        >
          <Typography fontSize={"0.91rem"} color={"#FFF"} fontWeight={400}>
            Book
          </Typography>
        </Box>
      </Stack>
    </MobileContainer>
  );
};

export default MobileScreen;
